import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./MessagingCenter.module.sass";
import ConversationList from "../ConversationList";
import MessageThread from "../MessageThread";
import GroupChatModal from "../GroupChatModal";
import NewMessageModal from "../NewMessageModal";
import { useChatContext } from "../../../contexts/ChatContext";
// Removed mock data; this component now exclusively uses Stream data

const MessagingCenter = ({ className, currentUserId, userType, noAirmail, collabRequestContext, onCollabRequestSent }) => {
    const {
        client,
        isConnected,
        channels,
        activeChannel,
        setActiveChannel,
        refreshChannels,
        startConversation,
        searchUsers
    } = useChatContext();

    const [showGroupModal, setShowGroupModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showConversationList, setShowConversationList] = useState(true);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [showNewMessageModal, setShowNewMessageModal] = useState(false);
    const [collabRequestSent, setCollabRequestSent] = useState(false);
    
    // Use Stream data if connected, otherwise use mock data
    const [activeConversationId, setActiveConversationId] = useState(null);

    const isUsingStream = isConnected && client;
    const conversations = channels;
    const currentActiveId = activeChannel?.id || activeConversationId;

    // Load data based on connection status
    useEffect(() => {
        if (isUsingStream) {
            // Refresh channels when Stream is connected
            refreshChannels();
        }
    }, [isUsingStream, refreshChannels]);
    
    // Handle responsive layout
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile && !currentActiveId && conversations.length > 0) {
                if (isUsingStream) {
                    setActiveChannel(conversations[0].id);
                } else {
                    setActiveConversationId(conversations[0].id);
                }
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentActiveId, conversations, isUsingStream, setActiveChannel]);

    // On mobile, automatically show the thread (hide conversation list) when a
    // channel is selected via deep link (e.g. from a notification click).
    useEffect(() => {
        if (isMobile && currentActiveId) {
            setShowConversationList(false);
        }
    }, [isMobile, currentActiveId]);
    
    const handleConversationSelect = useCallback((conversation) => {
        if (isUsingStream) {
            setActiveChannel(conversation.id);
        } else {
            setActiveConversationId(conversation.id);
        }
        
        // On mobile, hide conversation list when selecting a chat
        if (isMobile) {
            setShowConversationList(false);
        }
    }, [isUsingStream, setActiveChannel, isMobile]);
    
    const handleSendMessage = useCallback(async () => {
        // Stream handles message sending through the MessageThread component
        return;
    }, []);
    
    const handleNewMessage = useCallback(async () => {
        // Always open modal; don't populate any users initially
        setShowNewMessageModal(true);
        setUserSearchResults([]);
    }, []);
    
    const handleUserSearch = useCallback(async (searchTerm) => {
        const q = (searchTerm || '').trim();
        if (q.length < 2) {
            setUserSearchResults([]);
            return [];
        }
        if (!isUsingStream) {
            // When not connected to Stream yet, skip searching to avoid confusion
            setUserSearchResults([]);
            return [];
        }
        try {
            const results = await searchUsers(q);
            setUserSearchResults(results);
            return results; // Return the results so the modal knows when search is complete
        } catch (error) {
            console.error('Search failed:', error);
            setUserSearchResults([]);
            return [];
        }
    }, [isUsingStream, searchUsers]);
    
    const handleSelectUser = useCallback(async (user) => {
        if (!isUsingStream) return;

        try {
            await startConversation(user.id, user);
            setIsSearchingUsers(false);
            setShowNewMessageModal(false);
            setUserSearchResults([]);
            
            if (isMobile) {
                setShowConversationList(false);
            }
        } catch (error) {
            console.error('Failed to start conversation:', error);
        }
    }, [isUsingStream, startConversation, isMobile]);

    const handleNewGroup = () => {
        setShowGroupModal(true);
    };
    
    const handleCreateGroup = (groupData) => {
        // TODO: Implement group creation with Stream (creating a messaging channel with multiple members)
        console.log('Group creation with Stream to be implemented', groupData);
        setShowGroupModal(false);
    };
    
    // Try to find the active conversation in the formatted list
    let activeConversation = conversations.find(c => c.id === currentActiveId);
    
    // Defensive fallback: if activeChannel exists but isn't in the formatted list yet,
    // derive a minimal conversation object from the Stream channel state so the UI
    // can still render the thread instead of showing "Select a conversation"
    if (!activeConversation && activeChannel && currentActiveId) {
        try {
            const channelState = activeChannel.state;
            const members = channelState?.members ? Object.values(channelState.members) : [];
            const currentStreamUserId = client?.userID;
            const otherMembers = members.filter(m => m.user?.id !== currentStreamUserId);
            const primaryMember = otherMembers[0]?.user || {};
            
            activeConversation = {
                id: activeChannel.id,
                name: primaryMember.name || primaryMember.id || 'New Conversation',
                avatar: primaryMember.image || '/images/content/avatar-1.jpg',
                handle: primaryMember.handle || primaryMember.custom?.handle || null,
                first_name: primaryMember.first_name || primaryMember.custom?.firstName || 
                           (primaryMember.name ? primaryMember.name.split(' ')[0] : null),
                lastMessage: '',
                timestamp: activeChannel.data?.created_at || new Date().toISOString(),
                unreadCount: 0,
                isOnline: primaryMember.online || false,
                isGroup: otherMembers.length > 1,
                memberCount: members.length,
                members: members.map(m => m.user || {}),
                isTyping: false
            };
        } catch (err) {
            console.warn('Failed to derive fallback conversation from activeChannel:', err);
        }
    }
    
    const activeMessages = [];
    
    return (
        <div className={cn(styles.messagingCenter, className, { [styles.noAirmail]: noAirmail })}>
            <div className={cn(styles.container, {
                [styles.mobileShowThread]: isMobile && !showConversationList
            })}>
                <ConversationList
                    conversations={conversations}
                    activeConversationId={currentActiveId}
                    onConversationSelect={handleConversationSelect}
                    onNewMessage={handleNewMessage}
                    onNewGroup={handleNewGroup}
                    isSearchingUsers={isSearchingUsers}
                    userSearchResults={userSearchResults}
                    onUserSearch={handleUserSearch}
                    onSelectUser={handleSelectUser}
                    onCancelSearch={() => {
                        setIsSearchingUsers(false);
                        setUserSearchResults([]);
                    }}
                    className={cn({
                        [styles.mobileHidden]: isMobile && !showConversationList
                    })}
                />
                
                <MessageThread
                    conversation={activeConversation}
                    messages={activeMessages}
                    currentUserId={currentUserId}
                    onSendMessage={handleSendMessage}
                    isUsingStream={isUsingStream}
                    channelId={currentActiveId}
                    collabRequestContext={collabRequestContext}
                    onCollabRequestSent={() => {
                        setCollabRequestSent(true);
                        if (onCollabRequestSent) onCollabRequestSent();
                    }}
                    className={cn({
                        [styles.mobileHidden]: isMobile && showConversationList
                    })}
                />
            </div>
            
            {showGroupModal && (
                <GroupChatModal
                    onClose={() => setShowGroupModal(false)}
                    onCreate={handleCreateGroup}
                    userType={userType}
                />
            )}

            {showNewMessageModal && (
                <NewMessageModal
                    visible={showNewMessageModal}
                    onClose={() => { setShowNewMessageModal(false); setIsSearchingUsers(false); }}
                    onSearch={handleUserSearch}
                    onSelect={handleSelectUser}
                    results={userSearchResults}
                />
            )}
            
            {/* Mobile back button */}
            {isMobile && !showConversationList && (
                <button
                    className={styles.mobileBackBtn}
                    onClick={() => setShowConversationList(true)}
                >
                    Back to chats
                </button>
            )}
        </div>
    );
};

export default MessagingCenter;